import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FaTimes, FaCloudUploadAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { DOCUMENT_TYPE_OPTGROUPS } from '../constants/documentTypes';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
  workforceRoleLabel,
} from '../utils/workforcePeople';
import '../styles/BulkUpload.css';

const GET_ALL_USERS = `
  query GetAllUser($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        employeeCode
        roles
        role
      }
      totalPages
    }
  }
`;

const MAX_FILE_BYTES = 8 * 1024 * 1024;

const GENERATE_UPLOAD_URL = `
  mutation WorkforceUpload($userId: String!, $fileName: String!, $contentType: String!) {
    createWorkforceDocumentUploadUrl(userId: $userId, fileName: $fileName, contentType: $contentType) { key url }
  }
`;

const CREATE_WORKFORCE_DOCUMENT = `
  mutation CreateWorkforceDocument($input: CreateWorkforceDocumentInput!) {
    createWorkforceDocument(input: $input) {
      id
    }
  }
`;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const newRowId = () =>
  `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Bulk upload modes:
 * - employee: one employee → many files with different document types
 * - documentType: one document type → many employees each with a file
 */
export default function BulkUpload({
  onClose,
  onDone,
  initialMode = 'employee',
}) {
  const [mode, setMode] = useState(
    initialMode === 'documentType' ? 'documentType' : 'employee',
  );
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Shared fields
  const [cleaningLicense, setCleaningLicense] = useState('');
  const [experience, setExperience] = useState('');

  // Mode: employee
  const [employeeId, setEmployeeId] = useState('');
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [employeeRows, setEmployeeRows] = useState([
    { id: newRowId(), documentType: '', otherType: '', file: null },
  ]);

  // Mode: documentType
  const [documentType, setDocumentType] = useState('');
  const [otherDocumentType, setOtherDocumentType] = useState('');
  const [typeRows, setTypeRows] = useState([
    { id: newRowId(), employeeId: '', file: null },
  ]);

  const employeeBoxRef = useRef(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const allUsers = await fetchAllCompanyUsers(graphqlRequest, GET_ALL_USERS);
        const workforceUsers = allUsers.filter(isWorkforcePerson);
        const prefixCount = { ADM: 0, MGM: 0, SUP: 0, CLR: 0, CTR: 0, EMP: 0 };
        const mapped = workforceUsers
          .map((user) => {
            const name =
              `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
              'Unknown';
            const role = workforceRoleLabel(user.roles, user.role);
            let prefix = 'EMP';
            if (role === 'Admin') prefix = 'ADM';
            else if (role === 'Manager') prefix = 'MGM';
            else if (role === 'Supervisor') prefix = 'SUP';
            else if (role === 'Cleaner') prefix = 'CLR';
            else if (role === 'Contractor') prefix = 'CTR';
            const realCode = String(user.employeeCode || '')
              .trim()
              .toUpperCase();
            let code = realCode;
            if (!code) {
              prefixCount[prefix] += 1;
              code = `${prefix}${String(prefixCount[prefix]).padStart(3, '0')}`;
            }
            return {
              employeeId: code,
              dbId: user.id,
              name,
              role,
              label: `${code} — ${name} (${role})`,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setEmployees(mapped);
      } catch (err) {
        console.error(err);
        toast.error(err?.message || 'Failed to load employees');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!employeeBoxRef.current?.contains(e.target)) setEmployeeOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.employeeId === employeeId) || null,
    [employees, employeeId],
  );

  const filteredEmployees = useMemo(() => {
    const q = employeeQuery.trim().toLowerCase();
    if (!q || (selectedEmployee && employeeQuery === selectedEmployee.label)) {
      return employees;
    }
    return employees.filter(
      (emp) =>
        emp.employeeId.toLowerCase().includes(q) ||
        emp.name.toLowerCase().includes(q) ||
        emp.label.toLowerCase().includes(q),
    );
  }, [employees, employeeQuery, selectedEmployee]);

  const pickMainEmployee = (emp) => {
    setEmployeeId(emp.employeeId);
    setEmployeeQuery(emp.label);
    setEmployeeOpen(false);
  };

  const resolveDocType = (type, other) =>
    type === 'Other' ? String(other || '').trim() : String(type || '').trim();

  const validateFile = (file) => {
    if (!file) return 'Missing file';
    if (file.size > MAX_FILE_BYTES) return `${file.name} is over 8MB`;
    return '';
  };

  const uploadOne = async ({
    employee,
    documentType: docType,
    cleaningLicense: license,
    experienceYears,
    file,
  }) => {
    const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
      userId: employee.dbId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    });
    const target = uploadData?.createWorkforceDocumentUploadUrl;
    if (!target?.url || !target?.key) {
      throw new Error(`Failed to prepare upload for ${file.name}`);
    }
    let fileKey = target.key;
    try {
      await axios.put(target.url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
    } catch {
      if (file.size <= 1.5 * 1024 * 1024) {
        fileKey = await readFileAsDataUrl(file);
      } else {
        throw new Error(`Upload failed for ${file.name}`);
      }
    }
    await graphqlRequest(CREATE_WORKFORCE_DOCUMENT, {
      input: {
        userId: employee.dbId,
        employeeCode: employee.employeeId,
        employeeName: employee.name,
        documentType: docType,
        cleaningLicense: license,
        experienceYears: Number(experienceYears),
        fileName: file.name,
        fileType: file.type || '',
        fileSize: Number(file.size || 0),
        fileKey,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cleaningLicense) {
      toast.error('Please select cleaning license status');
      return;
    }
    const exp = Number(experience);
    if (!Number.isFinite(exp) || exp < 0) {
      toast.error('Please enter valid years of experience');
      return;
    }

    /** @type {Array<{ employee: object, documentType: string, file: File }>} */
    const jobs = [];

    if (mode === 'employee') {
      if (!selectedEmployee) {
        toast.error('Please select an employee');
        return;
      }
      for (const row of employeeRows) {
        const type = resolveDocType(row.documentType, row.otherType);
        if (!type) {
          toast.error('Each row needs a document type');
          return;
        }
        const fileErr = validateFile(row.file);
        if (fileErr) {
          toast.error(fileErr);
          return;
        }
        jobs.push({
          employee: selectedEmployee,
          documentType: type,
          file: row.file,
        });
      }
    } else {
      const type = resolveDocType(documentType, otherDocumentType);
      if (!type) {
        toast.error('Please select a document type');
        return;
      }
      for (const row of typeRows) {
        const emp = employees.find((x) => x.employeeId === row.employeeId);
        if (!emp) {
          toast.error('Each row needs an employee');
          return;
        }
        const fileErr = validateFile(row.file);
        if (fileErr) {
          toast.error(fileErr);
          return;
        }
        jobs.push({ employee: emp, documentType: type, file: row.file });
      }
    }

    if (!jobs.length) {
      toast.error('Add at least one file to upload');
      return;
    }

    setUploading(true);
    setProgress({ done: 0, total: jobs.length });
    let ok = 0;
    try {
      for (let i = 0; i < jobs.length; i += 1) {
        const job = jobs[i];
        await uploadOne({
          employee: job.employee,
          documentType: job.documentType,
          cleaningLicense,
          experienceYears: exp,
          file: job.file,
        });
        ok += 1;
        setProgress({ done: ok, total: jobs.length });
      }
      toast.success(
        mode === 'employee'
          ? `Uploaded ${ok} document(s) for ${selectedEmployee.name}`
          : `Uploaded ${ok} ${jobs[0].documentType} document(s) across employees`,
      );
      if (onDone) await onDone();
      window.setTimeout(() => {
        window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
        window.dispatchEvent(new Event('workforce-documents-refresh'));
      }, 400);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message ||
          `Upload stopped after ${ok} of ${jobs.length}. Please try again.`,
      );
    } finally {
      setUploading(false);
    }
  };

  const DocTypeSelect = useCallback(({ value, onChange, id }) => (
    <select
      id={id}
      value={value}
      onChange={onChange}
      className="bulk-upload-select"
      required
    >
      <option value="">Document type</option>
      {DOCUMENT_TYPE_OPTGROUPS.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  ), []);

  return (
    <div className="bulk-upload-overlay" onClick={onClose}>
      <div
        className="bulk-upload-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-upload-title"
      >
        <div className="bulk-upload-header">
          <h3 id="bulk-upload-title">Bulk Upload</h3>
          <button type="button" className="bulk-upload-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form className="bulk-upload-body" onSubmit={handleSubmit}>
          <div className="bulk-upload-modes" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'employee'}
              className={`bulk-upload-mode${mode === 'employee' ? ' active' : ''}`}
              onClick={() => setMode('employee')}
              disabled={uploading}
            >
              By employee
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'documentType'}
              className={`bulk-upload-mode${
                mode === 'documentType' ? ' active' : ''
              }`}
              onClick={() => setMode('documentType')}
              disabled={uploading}
            >
              By document type
            </button>
          </div>

          <p className="bulk-upload-hint">
            {mode === 'employee'
              ? 'Pick one employee, then add many files — each with its own document type (e.g. Passport, WWCC, Police Check).'
              : 'Pick one document type, then upload that same type for many employees (one file per employee).'}
          </p>

          {mode === 'employee' ? (
            <>
              <label className="bulk-upload-label">
                Employee <span>*</span>
              </label>
              <div className="bulk-upload-combo" ref={employeeBoxRef}>
                <input
                  type="text"
                  className="bulk-upload-input"
                  value={employeeQuery}
                  disabled={loading || uploading}
                  placeholder={
                    loading ? 'Loading employees…' : 'Search name or ID'
                  }
                  onChange={(e) => {
                    setEmployeeQuery(e.target.value);
                    setEmployeeOpen(true);
                    if (
                      selectedEmployee &&
                      e.target.value !== selectedEmployee.label
                    ) {
                      setEmployeeId('');
                    }
                  }}
                  onFocus={() => setEmployeeOpen(true)}
                  autoComplete="off"
                />
                {employeeOpen && !loading ? (
                  <ul className="bulk-upload-combo-list">
                    {filteredEmployees.length === 0 ? (
                      <li className="bulk-upload-empty">No employees found</li>
                    ) : (
                      filteredEmployees.map((emp) => (
                        <li key={emp.dbId}>
                          <button
                            type="button"
                            onClick={() => pickMainEmployee(emp)}
                          >
                            <strong>{emp.employeeId}</strong>
                            <span>{emp.name}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <label className="bulk-upload-label">
                Document type <span>*</span>
              </label>
              <DocTypeSelect
                value={documentType}
                onChange={(e) => {
                  setDocumentType(e.target.value);
                  if (e.target.value !== 'Other') setOtherDocumentType('');
                }}
              />
              {documentType === 'Other' ? (
                <input
                  type="text"
                  className="bulk-upload-input"
                  placeholder="Enter custom document type"
                  value={otherDocumentType}
                  onChange={(e) => setOtherDocumentType(e.target.value)}
                  disabled={uploading}
                  required
                />
              ) : null}
            </>
          )}

          <div className="bulk-upload-shared">
            <label>
              Cleaning license <span>*</span>
              <select
                className="bulk-upload-select"
                value={cleaningLicense}
                onChange={(e) => setCleaningLicense(e.target.value)}
                disabled={uploading}
                required
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
            <label>
              Experience (years) <span>*</span>
              <input
                type="number"
                className="bulk-upload-input"
                min="0"
                max="60"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                disabled={uploading}
                required
              />
            </label>
          </div>

          <div className="bulk-upload-rows-head">
            <strong>
              {mode === 'employee' ? 'Documents for this employee' : 'Employees'}
            </strong>
            <button
              type="button"
              className="bulk-upload-add-row"
              disabled={uploading}
              onClick={() => {
                if (mode === 'employee') {
                  setEmployeeRows((prev) => [
                    ...prev,
                    {
                      id: newRowId(),
                      documentType: '',
                      otherType: '',
                      file: null,
                    },
                  ]);
                } else {
                  setTypeRows((prev) => [
                    ...prev,
                    { id: newRowId(), employeeId: '', file: null },
                  ]);
                }
              }}
            >
              <FaPlus /> Add row
            </button>
          </div>

          <div className="bulk-upload-rows">
            {mode === 'employee'
              ? employeeRows.map((row, index) => (
                  <div key={row.id} className="bulk-upload-row">
                    <span className="bulk-upload-row-num">{index + 1}</span>
                    <div className="bulk-upload-row-fields">
                      <DocTypeSelect
                        value={row.documentType}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmployeeRows((prev) =>
                            prev.map((r) =>
                              r.id === row.id
                                ? {
                                    ...r,
                                    documentType: value,
                                    otherType:
                                      value === 'Other' ? r.otherType : '',
                                  }
                                : r,
                            ),
                          );
                        }}
                      />
                      {row.documentType === 'Other' ? (
                        <input
                          type="text"
                          className="bulk-upload-input"
                          placeholder="Custom type"
                          value={row.otherType}
                          onChange={(e) =>
                            setEmployeeRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? { ...r, otherType: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          disabled={uploading}
                        />
                      ) : null}
                      <div className="bulk-upload-file-pick">
                        <input
                          ref={(el) => {
                            fileInputRefs.current[row.id] = el;
                          }}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            if (file && file.size > MAX_FILE_BYTES) {
                              toast.error('File must be under 8MB');
                              e.target.value = '';
                              return;
                            }
                            setEmployeeRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, file } : r,
                              ),
                            );
                          }}
                        />
                        <button
                          type="button"
                          className="bulk-upload-file-btn"
                          disabled={uploading}
                          onClick={() =>
                            fileInputRefs.current[row.id]?.click()
                          }
                        >
                          <FaCloudUploadAlt />{' '}
                          {row.file ? 'Change file' : 'Choose file'}
                        </button>
                        <span className="bulk-upload-file-name">
                          {row.file?.name || 'No file'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="bulk-upload-remove"
                      disabled={uploading || employeeRows.length <= 1}
                      title="Remove row"
                      onClick={() =>
                        setEmployeeRows((prev) =>
                          prev.filter((r) => r.id !== row.id),
                        )
                      }
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))
              : typeRows.map((row, index) => (
                  <div key={row.id} className="bulk-upload-row">
                    <span className="bulk-upload-row-num">{index + 1}</span>
                    <div className="bulk-upload-row-fields">
                      <select
                        className="bulk-upload-select"
                        value={row.employeeId}
                        disabled={loading || uploading}
                        onChange={(e) =>
                          setTypeRows((prev) =>
                            prev.map((r) =>
                              r.id === row.id
                                ? { ...r, employeeId: e.target.value }
                                : r,
                            ),
                          )
                        }
                        required
                      >
                        <option value="">Select employee</option>
                        {employees.map((emp) => (
                          <option key={emp.dbId} value={emp.employeeId}>
                            {emp.label}
                          </option>
                        ))}
                      </select>
                      <div className="bulk-upload-file-pick">
                        <input
                          ref={(el) => {
                            fileInputRefs.current[row.id] = el;
                          }}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            if (file && file.size > MAX_FILE_BYTES) {
                              toast.error('File must be under 8MB');
                              e.target.value = '';
                              return;
                            }
                            setTypeRows((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, file } : r,
                              ),
                            );
                          }}
                        />
                        <button
                          type="button"
                          className="bulk-upload-file-btn"
                          disabled={uploading}
                          onClick={() =>
                            fileInputRefs.current[row.id]?.click()
                          }
                        >
                          <FaCloudUploadAlt />{' '}
                          {row.file ? 'Change file' : 'Choose file'}
                        </button>
                        <span className="bulk-upload-file-name">
                          {row.file?.name || 'No file'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="bulk-upload-remove"
                      disabled={uploading || typeRows.length <= 1}
                      title="Remove row"
                      onClick={() =>
                        setTypeRows((prev) =>
                          prev.filter((r) => r.id !== row.id),
                        )
                      }
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
          </div>

          {uploading ? (
            <p className="bulk-upload-progress">
              Uploading {progress.done} / {progress.total}…
            </p>
          ) : null}

          <div className="bulk-upload-footer">
            <button
              type="button"
              className="bulk-upload-cancel"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bulk-upload-submit"
              disabled={uploading || loading}
            >
              {uploading ? 'Uploading…' : 'Upload all'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
